/* eslint max-lines-per-function: ["error", {max: 230}] -- many user-related GET endpoints */
/* eslint max-lines: ["error", {max: 500}] -- many user-related GET endpoints */
/* eslint-disable max-params -- these linting errors come up constantly with reducers */
import isNil from "lodash/isNil";

import { DeviceType } from "@/constants/User";
import { isUserInTreatment } from "@/hooks/usePatientStatus";
import api, { CacheTagType } from "@/store/api";
import {
  setAppSupportLinkedPatientsAction,
  setAppUserBasicInfoAction,
  setAppUserUniversalStatusCodeAction,
  setIsAppUserInTreatmentAction,
} from "@/store/reducers/appUser";
import {
  transformContentFeedResponse,
  transformSafetyPlanDetailsResponse,
  updateLinkedPatientProfileCallback,
  updateProfileCallback,
  updateProfileNotificationsCallback,
} from "@/utils/api/user.util";

enum UserEndpoint {
  Avatar = "getAvatar",
  BasicInfo = "getBasicInfo",
  ConsentsInfo = "getConsentsInfo",
  ContentFeed = "getContentFeed",
  FullProfile = "getFullProfile",
  LinkedPatientsTags = "getLinkedPatientsTags",
  PatientTags = "getPatientTags",
  RelationToPatient = "getRelationsToPatient",
  SafetyPlanDetails = "getSafetyPlanDetails",
  SafetyPlanExists = "getSafetyPlanExists",
}

type UserRoleAndIdRequest = {
  isPatient: boolean;
  userExternalId: string;
};

interface UpdateUserTermsAndPrivacyPolicyConsentRequest {
  consentName: string;
  consentVersion: string;
}

interface UpdateCalendarPreferencesRequest extends UserRoleAndIdRequest {
  isDeviceCalendarEnabled?: boolean;
  isGoogleCalendarEnabled?: boolean;
}

interface UpdateUserNotificationsSettingsRequest extends UserRoleAndIdRequest {
  body: UpdateNotificationSettingsBody;
}

interface ContentFeedRequest extends UserRoleAndIdRequest {
  isPromoted?: boolean;
}

interface UpdateUserDeviceLoginStateRequest {
  isLoggedIn: boolean;
  os: string;
  type: DeviceType;
}

interface GetUserAvatarResponse {
  url?: string;
}

const ApiWithQueries = api.injectEndpoints({
  endpoints: (build) => ({
    [UserEndpoint.Avatar]: build.query<string | undefined, string>({
      providesTags: (providerExternalId) => [
        { id: providerExternalId, type: CacheTagType.Avatar },
      ],
      query: (providerExternalId: string) => ({
        // Keep avatar data cached for entire session until logout
        keepUnusedDataFor: Infinity,
        method: "GET",
        url: `/users/family/${providerExternalId}/avatar`,
      }),
      transformResponse: (
        response: GetUserAvatarResponse | undefined,
      ): string | undefined => response?.url,
    }),
    [UserEndpoint.BasicInfo]: build.query<BasicUserInfoResponse, void>({
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(
          setAppUserBasicInfoAction({
            chosenName: data.chosenName,
            dateOfBirth: data.dateOfBirth,
            externalId: data.externalId,
            firstName: data.firstName,
            hasGivenRecordingConsent: data.hasGivenRecordingConsent,
            isPatient: data.isPatient,
            lastName: data.lastName,
            onboardedAt: data.onboardedAt,
            onboardings: data.onboardings,
          }),
        );
      },
      providesTags: [CacheTagType.BasicInfo],
      query: () => "/users/family/self/v1",
    }),

    [UserEndpoint.ConsentsInfo]: build.query<Consent[], void>({
      providesTags: [CacheTagType.Consents],
      query: () => "/users/family/consent/v1",
    }),
    [UserEndpoint.ContentFeed]: build.query<
      ResourceTheme[],
      ContentFeedRequest
    >({
      query: ({ isPatient, isPromoted, userExternalId }) => ({
        method: "GET",
        params: { isPromoted: isPromoted ?? false },
        url: `/users/family/${isPatient ? "patients" : "supports"}/${userExternalId}/content/feed`,
      }),
      transformResponse: transformContentFeedResponse,
    }),
    [UserEndpoint.FullProfile]: build.query<FullProfileResponse, void>({
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        const universalStatus = data.universalStatusCode;
        if (!isNil(universalStatus)) {
          dispatch(setAppUserUniversalStatusCodeAction(universalStatus));
        }

        if (data.isPatient) {
          await dispatch(
            userApi.endpoints[UserEndpoint.PatientTags].initiate(
              data.externalId,
            ),
          );
          const isCurrentUserInTreatment =
            !isNil(universalStatus) && isUserInTreatment(universalStatus);
          dispatch(setIsAppUserInTreatmentAction(isCurrentUserInTreatment));
          return;
        }

        dispatch(setAppSupportLinkedPatientsAction(data.linkedPatients || []));
      },
      providesTags: [CacheTagType.FullProfile],
      query: () => "/users/family/profile/v1",
    }),

    [UserEndpoint.LinkedPatientsTags]: build.query<
      LinkedPatientsTagsResponse,
      string[]
    >({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      queryFn: async (ids: string[], { dispatch, forced }) => {
        const promises = ids.map(async (id) => {
          const response = await dispatch(
            userApi.endpoints[UserEndpoint.PatientTags].initiate(id, {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              forceRefetch: forced,
            }),
          );

          return response.data;
        });

        const result = await Promise.all(promises);

        const linkedPatientsTags = ids.reduce(
          (acc: LinkedPatientsTagsResponse, id, index) => {
            const tags =
              isNil(result) || isNil(result[index]) ? [] : result[index].data;
            // eslint-disable-next-line no-param-reassign
            acc[id] = tags;
            return acc;
          },
          {},
        );

        return {
          data: linkedPatientsTags,
        };
      },
    }),
    [UserEndpoint.PatientTags]: build.query<PatientTagsResponse, string>({
      query: (patientExternalId) =>
        `/users/family/${patientExternalId}/tags/v1`,
    }),

    [UserEndpoint.SafetyPlanDetails]: build.query<SafetyPlanDetails, string>({
      query: (patientExternalId: string) => {
        return {
          url: `/surveys/family/safety-plan/${patientExternalId}`,
        };
      },
      transformResponse: transformSafetyPlanDetailsResponse,
    }),
    [UserEndpoint.SafetyPlanExists]: build.query<
      Record<string, boolean>,
      string[]
    >({
      query: (ids: string[]) => {
        return {
          params: { ids },
          url: "/surveys/family/safety-plan/exists",
        };
      },
      transformResponse: (response: SafetyPlanExistsResponse) => response.data,
    }),
  }),
});

// since some mutations invoke some of these queries in their own onQueryStarted,
// we inject mutations after having injected the queries, to ensure the queries are initialized
const ApiWithQueriesAndMutations = ApiWithQueries.injectEndpoints({
  endpoints: (build) => ({
    revokeAuthToken: build.mutation<void, void>({
      // user is implicitly identified from the authorization bearer token attached to every API request
      query: () => ({
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        responseHandler: "text",
        url: "/users/logout",
      }),
    }),
    updateCalendarPreferences: build.mutation<
      string,
      UpdateCalendarPreferencesRequest
    >({
      invalidatesTags: [CacheTagType.FullProfile],
      query: ({
        isDeviceCalendarEnabled,
        isGoogleCalendarEnabled,
        isPatient,
        userExternalId,
      }) => ({
        body: {
          ...(isDeviceCalendarEnabled !== undefined && {
            // eslint-disable-next-line @typescript-eslint/naming-convention -- matches API field name
            deviceCalendarEnabled: isDeviceCalendarEnabled,
          }),
          ...(isGoogleCalendarEnabled !== undefined && {
            // eslint-disable-next-line @typescript-eslint/naming-convention -- matches API field name
            googleCalendarEnabled: isGoogleCalendarEnabled,
          }),
        },
        method: "PUT",
        responseHandler: "text",
        url: `/users/family/${isPatient ? "patients" : "supports"}/${userExternalId}/preferences`,
      }),
    }),
    updateLinkedPatientProfile: build.mutation<
      FullProfileResponse,
      UpdateLinkedPatientProfileRequest
    >({
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        // optimistically manually update the FullProfileResponse cache to reflect the updated payload
        // as the FullProfile API call being re-made takes ~5 seconds in total
        const patchResult = await dispatch(
          ApiWithQueries.util.updateQueryData(
            UserEndpoint.FullProfile,
            undefined,
            updateLinkedPatientProfileCallback(arg),
          ),
        );

        try {
          await queryFulfilled;
          // only AFTER the manual update, invalidate the FullProfile tag, aka the current cache
          // if this endpoint instead had an invalidateTags: [...] property, it would effectively
          // undo the above manual cache edit preemptively, meaning waiting for that ~5 seconds API call again

          // NOTE: this is necessary as the FullProfile endpoint is responsible for updating redux global state around FullProfile/LinkedPatients
          // BasicInfo also needs to be invalidated as the actual user's profile details have changed, so their basic info details likely have as well
          await dispatch(
            api.util.invalidateTags([
              CacheTagType.BasicInfo,
              CacheTagType.FullProfile,
            ]),
          );
        } catch {
          // if the API call failed, rollback the manual cache update, as it did not succeed/go through
          patchResult.undo();
        }
      },
      query: ({ body, patientExternalId }) => ({
        body,
        method: "PUT",
        responseHandler: "text",
        url: `/users/family/patient/${patientExternalId}/v1`,
      }),
    }),
    updateSupportUserProfile: build.mutation<
      FullProfileResponse,
      UpdateProfileRequest
    >({
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        // optimistically manually update the FullProfileResponse cache to reflect the updated payload
        // as the FullProfile API call being re-made takes ~5 seconds in total
        const patchResult = await dispatch(
          ApiWithQueries.util.updateQueryData(
            UserEndpoint.FullProfile,
            undefined,
            updateProfileCallback(arg),
          ),
        );

        try {
          await queryFulfilled;
          // only AFTER the manual update, invalidate the FullProfile tag, aka the current cache
          // if this endpoint instead had an invalidateTags: [...] property, it would effectively
          // undo the above manual cache edit preemptively, meaning waiting for that ~5 seconds API call again

          // NOTE: this is necessary as the FullProfile endpoint is responsible for updating redux global state around FullProfile/LinkedPatients
          // BasicInfo also needs to be invalidated as the actual user's profile details have changed, so their basic info details likely have as well
          await dispatch(
            api.util.invalidateTags([
              CacheTagType.BasicInfo,
              CacheTagType.FullProfile,
            ]),
          );
        } catch {
          // if the API call failed, rollback the manual cache update, as it did not succeed/go through
          patchResult.undo();
        }
      },
      query: (body) => ({
        body,
        method: "PUT",
        responseHandler: "text",
        url: "/users/family/support/v1",
      }),
    }),
    updateUserDeviceLoginState: build.mutation<
      string,
      UpdateUserDeviceLoginStateRequest
    >({
      query: ({ isLoggedIn, os, type }) => ({
        body: {
          isLoggedIn,
          os,
          type,
        },
        method: "PUT",
        responseHandler: "text",
        url: "/users/family/device",
      }),
    }),
    updateUserNotificationSettings: build.mutation<
      string,
      UpdateUserNotificationsSettingsRequest
    >({
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        // optimistically manually update the FullProfileResponse cache to reflect the updated payload
        // as the FullProfile API call being re-made takes ~5 seconds in total
        const patchResult = await dispatch(
          ApiWithQueries.util.updateQueryData(
            UserEndpoint.FullProfile,
            undefined,
            updateProfileNotificationsCallback(arg.body),
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          // if the API call failed, rollback the manual cache update, as it did not succeed/go through
          patchResult.undo();
        }
      },
      query: ({ body, isPatient, userExternalId }) => ({
        body,
        method: "PUT",
        responseHandler: "text",
        url: `/users/family/${isPatient ? "patients" : "supports"}/${userExternalId}/preferences`,
      }),
    }),
    updateUserOnboardedStatus: build.mutation<string, void>({
      invalidatesTags: [CacheTagType.BasicInfo],
      onQueryStarted: async (_arg, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch {
          throw new Error("Failed to update onboarded status");
        }
      },
      query: () => ({
        body: {
          deviceType: DeviceType.Mobile,
          onboardedAt: new Date().toISOString(),
        },
        method: "POST",
        responseHandler: "text",
        url: "/users/family/onboard/v1",
      }),
    }),
    updateUserRecordingConsent: build.mutation<
      string,
      { hasGivenRecordingConsent: boolean; patientExternalId: string }
    >({
      invalidatesTags: [CacheTagType.FullProfile],
      query: ({ hasGivenRecordingConsent, patientExternalId }) => ({
        body: { hasGivenRecordingConsent },
        method: "PUT",
        responseHandler: "text",
        url: `/users/family/patient/${patientExternalId}/v1`,
      }),
    }),
    updateUserTermsAndPrivacyPolicyConsent: build.mutation<
      string,
      UpdateUserTermsAndPrivacyPolicyConsentRequest[]
    >({
      invalidatesTags: [CacheTagType.Consents],
      query: (consents) => ({
        body: { consents },
        method: "POST",
        responseHandler: "text",
        url: "/users/family/consent/v1",
      }),
    }),
  }),
});

const userApi = ApiWithQueriesAndMutations;

// eslint-disable-next-line no-restricted-syntax -- large API
const {
  useGetAvatarQuery,
  useGetBasicInfoQuery,
  useGetConsentsInfoQuery,
  useGetFullProfileQuery,
  useGetSafetyPlanDetailsQuery,
  useGetSafetyPlanExistsQuery,
  useLazyGetContentFeedQuery,
  useLazyGetSafetyPlanDetailsQuery,
  useRevokeAuthTokenMutation,
  useUpdateCalendarPreferencesMutation,
  useUpdateLinkedPatientProfileMutation,
  useUpdateSupportUserProfileMutation,
  useUpdateUserDeviceLoginStateMutation,
  useUpdateUserNotificationSettingsMutation,
  useUpdateUserOnboardedStatusMutation,
  useUpdateUserRecordingConsentMutation,
  useUpdateUserTermsAndPrivacyPolicyConsentMutation,
} = userApi;

export {
  useGetAvatarQuery,
  useGetBasicInfoQuery,
  useGetConsentsInfoQuery,
  useGetFullProfileQuery,
  useGetSafetyPlanDetailsQuery,
  useGetSafetyPlanExistsQuery,
  useLazyGetContentFeedQuery,
  useLazyGetSafetyPlanDetailsQuery,
  useRevokeAuthTokenMutation,
  useUpdateCalendarPreferencesMutation,
  useUpdateLinkedPatientProfileMutation,
  useUpdateSupportUserProfileMutation,
  useUpdateUserDeviceLoginStateMutation,
  useUpdateUserNotificationSettingsMutation,
  useUpdateUserOnboardedStatusMutation,
  useUpdateUserRecordingConsentMutation,
  useUpdateUserTermsAndPrivacyPolicyConsentMutation,
};
